import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, StarIcon, ClockIcon, DollarSignIcon, UsersIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function ServicesPage() {
  return <div className="w-full" data-id="element-721">
      {/* HERO */}
      <section className="bg-gradient-to-br from-nrs-navy to-nrs-blue py-16 px-4" data-id="element-722">
        <div className="max-w-7xl mx-auto text-center" data-id="element-723">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-724">
            <div className="inline-block px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-4" data-id="element-725">
              Complete ServiceNow Services
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-white mb-4" data-id="element-726">
              Governance + Development.
              <br data-id="element-727" />
              <span className="text-nrs-orange" data-id="element-728">
                Everything Under One Roof.
              </span>
            </h1>
            <p className="text-blue-200 text-lg max-w-3xl mx-auto" data-id="element-729">
              Most clients engage us for both governance and development. Choose
              the services that fit your needs—or let us design a complete
              platform stewardship program.
            </p>
          </motion.div>
        </div>
      </section>

      {/* GOVERNANCE SERVICES */}
      <section className="bg-white py-16 px-4" data-id="element-730">
        <div className="max-w-7xl mx-auto" data-id="element-731">
          <div className="flex items-center gap-4 mb-10" data-id="element-732">
            <div className="h-10 w-1.5 bg-nrs-orange rounded-full" data-id="element-733" />
            <div data-id="element-734">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy" data-id="element-735">
                Governance Services
              </h2>
              <p className="text-gray-500 text-sm mt-1" data-id="element-736">
                Strategic oversight, policy frameworks, and platform governance
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-737">
            {/* Card 1 */}
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-738">
              <div className="bg-nrs-light px-6 py-5 border-b border-gray-200" data-id="element-739">
                <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-740">
                  3-Month Engagement
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy" data-id="element-741">
                  Governance Foundations + Application Rationalization
                </h3>
                <div className="mt-3 flex items-center gap-2" data-id="element-742">
                  <DollarSignIcon className="h-4 w-4 text-nrs-orange" data-id="element-743" />
                  <span className="font-heading font-bold text-nrs-orange" data-id="element-744">
                    $65K–95K
                  </span>
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-745">
                <p className="text-gray-600 text-sm mb-4" data-id="element-746">
                  For agencies with a new platform OR established platform with
                  application sprawl and no governance framework.
                </p>
                <div className="space-y-4" data-id="element-747">
                  <div data-id="element-748">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-749">
                      Technical Policy Governance
                    </div>
                    <ul className="space-y-1" data-id="element-750">
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-751">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-752" />
                        Architecture standards and review process
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-753">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-754" />
                        Coding standards and best practices enforcement
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-755">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-756" />
                        Technical debt inventory with upgrade impact scoring
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-757">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-758" />
                        Upgrade roadmap and safety validation
                      </li>
                    </ul>
                  </div>
                  <div data-id="element-759">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-760">
                      Data Governance
                    </div>
                    <ul className="space-y-1" data-id="element-761">
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-762">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-763" />
                        Master data ownership framework
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-764">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-765" />
                        Integration architecture patterns and standards
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-766">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-767" />
                        Cross-domain sync strategy (NIPR/JWICS)
                      </li>
                    </ul>
                  </div>
                  <div data-id="element-768">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-769">
                      Application Rationalization
                    </div>
                    <ul className="space-y-1" data-id="element-770">
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-771">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-772" />
                        Complete inventory of all agency applications (50-200
                        systems)
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-773">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-774" />
                        ServiceNow fit analysis per application
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-775">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-776" />
                        Migration roadmap with prioritization
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-777">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-778" />
                        Business cases for top 10 candidates
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-5 bg-green-50 rounded-lg p-3 border border-green-100" data-id="element-779">
                  <p className="text-xs text-green-800 font-medium" data-id="element-780">
                    Outcome: You know what to build, how to build it, whether
                    ServiceNow is the right platform, and who should build it.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-781">
              <div className="bg-nrs-light px-6 py-5 border-b border-gray-200" data-id="element-782">
                <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-783">
                  Ongoing Retainer
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy" data-id="element-784">
                  Fractional Platform Owner
                </h3>
                <div className="mt-3 flex items-center gap-2" data-id="element-785">
                  <DollarSignIcon className="h-4 w-4 text-nrs-orange" data-id="element-786" />
                  <span className="font-heading font-bold text-nrs-orange" data-id="element-787">
                    $10K–14K/month
                  </span>
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-788">
                <p className="text-gray-600 text-sm mb-4" data-id="element-789">
                  For agencies with an established platform needing continuous
                  governance oversight.
                </p>
                <ul className="space-y-2" data-id="element-790">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-791">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-792" />
                    10-15 hours/week embedded governance advisor
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-793">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-794" />
                    Technical Policy, Data, Security/ATO, and Strategic
                    Governance
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-795">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-796" />
                    Monthly governance board facilitation
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-797">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-798" />
                    Quarterly maturity assessments
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-799">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-800" />
                    SI/vendor oversight (works with NRS development team OR
                    external SIs)
                  </li>
                </ul>
                <div className="mt-5 bg-blue-50 rounded-lg p-3 border border-blue-100" data-id="element-801">
                  <p className="text-xs text-nrs-blue font-medium" data-id="element-802">
                    Annual contract. Senior practitioner. ~$100K/year vs. $200K
                    for a full-time hire.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 3 */}
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-803">
              <div className="bg-nrs-light px-6 py-5 border-b border-gray-200" data-id="element-804">
                <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-805">
                  Governance Automation
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy" data-id="element-806">
                  Platform Command Center
                </h3>
                <div className="mt-3 space-y-1" data-id="element-807">
                  <div className="flex items-center gap-2" data-id="element-808">
                    <DollarSignIcon className="h-4 w-4 text-nrs-orange" data-id="element-809" />
                    <span className="font-heading font-bold text-nrs-orange" data-id="element-810">
                      $95K–135K
                    </span>
                    <span className="text-gray-500 text-xs" data-id="element-811">build</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-812">
                    <DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-813" />
                    <span className="font-semibold text-gray-600 text-sm" data-id="element-814">
                      $3K–5K/month
                    </span>
                    <span className="text-gray-500 text-xs" data-id="element-815">support</span>
                  </div>
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-816">
                <p className="text-gray-600 text-sm mb-4" data-id="element-817">
                  Custom scoped application with automated governance tooling
                  built directly into your ServiceNow instance.
                </p>
                <ul className="space-y-2" data-id="element-818">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-819">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-820" />
                    Automated demand scoring dashboard
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-821">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-822" />
                    Governance maturity tracker
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-823">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-824" />
                    Technical debt ledger with scoring
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-825">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-826" />
                    ATO compliance dashboard
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-827">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-828" />
                    Data governance registry
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-829">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-830" />
                    Application rationalization tracker
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DEVELOPMENT SERVICES */}
      <section className="bg-nrs-light py-16 px-4" data-id="element-831">
        <div className="max-w-7xl mx-auto" data-id="element-832">
          <div className="flex items-center gap-4 mb-10" data-id="element-833">
            <div className="h-10 w-1.5 bg-nrs-blue rounded-full" data-id="element-834" />
            <div data-id="element-835">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy" data-id="element-836">
                Development Services
              </h2>
              <p className="text-gray-500 text-sm mt-1" data-id="element-837">
                Expert ServiceNow implementation, operations, and specialized
                development
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-838">
            {/* Platform Dev */}
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl overflow-hidden border border-gray-200" data-id="element-839">
              <div className="bg-nrs-blue px-6 py-5" data-id="element-840">
                <div className="text-blue-200 font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-841">
                  Full Implementation
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-842">
                  Platform Development & Integration
                </h3>
                <div className="mt-2 flex items-center gap-4" data-id="element-843">
                  <span className="text-nrs-orange font-bold" data-id="element-844">$150K–500K</span>
                  <span className="text-blue-200 text-sm" data-id="element-845">3-9 months</span>
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-846">
                <p className="text-gray-600 text-sm mb-4" data-id="element-847">
                  For agencies needing ServiceNow implementation, module
                  buildout, or major enhancements.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-848">
                  <div data-id="element-849">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-850">
                      Core Modules
                    </div>
                    <ul className="space-y-1 text-xs text-gray-600" data-id="element-851">
                      <li data-id="element-852">• ITSM (Incident, Problem, Change, CMDB)</li>
                      <li data-id="element-853">• HRSD (Onboarding, Case Mgmt, ESC)</li>
                      <li data-id="element-854">• Security Operations (VR, TI, SIR)</li>
                      <li data-id="element-855">• SPM (Demand, Project, Agile)</li>
                      <li data-id="element-856">• CSM (Case Mgmt, Portals)</li>
                      <li data-id="element-857">• ITOM (Discovery, Service Mapping)</li>
                    </ul>
                  </div>
                  <div data-id="element-858">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-859">
                      Specialized
                    </div>
                    <ul className="space-y-1 text-xs text-gray-600" data-id="element-860">
                      <li data-id="element-861">• Cross-Domain (NIPR/JWICS)</li>
                      <li data-id="element-862">• Custom Applications</li>
                      <li data-id="element-863">• Complex Integrations</li>
                      <li data-id="element-864">• Data Migration</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-1.5" data-id="element-865">
                  <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-866">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" data-id="element-867" />
                    Self-Governing Development: built-in architecture review
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-868">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" data-id="element-869" />
                    Agile Delivery: bi-weekly sprints
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-870">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" data-id="element-871" />
                    Upgrade-Safe Code: out-of-box prioritized
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Platform Ops */}
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl overflow-hidden border border-gray-200" data-id="element-872">
              <div className="bg-nrs-blue px-6 py-5" data-id="element-873">
                <div className="text-blue-200 font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-874">
                  Managed Services
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-875">
                  Platform Operations & Maintenance
                </h3>
                <div className="mt-2 flex items-center gap-4" data-id="element-876">
                  <span className="text-nrs-orange font-bold" data-id="element-877">
                    $120K–250K/year
                  </span>
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-878">
                <p className="text-gray-600 text-sm mb-4" data-id="element-879">
                  Day-to-day administration and ongoing enhancements with
                  governance included.
                </p>
                <ul className="space-y-2 mb-4" data-id="element-880">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-881">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-882" />
                    User provisioning and incident support (P1-P4 with SLAs)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-883">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-884" />
                    Monthly feature releases
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-885">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-886" />
                    Governance included (architecture reviews, data governance,
                    ATO compliance)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-887">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-888" />
                    Platform monitoring and optimization
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-889">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-890" />
                    License management
                  </li>
                </ul>
                <div className="bg-nrs-light rounded-lg p-3 border border-nrs-blue/20" data-id="element-891">
                  <div className="font-heading font-semibold text-xs text-nrs-blue mb-1" data-id="element-892">
                    SLA Commitments
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-893">
                    P1: 2-hour response / 8-hour resolution
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Specialized */}
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl overflow-hidden border border-gray-200 lg:col-span-2" data-id="element-894">
              <div className="bg-gray-800 px-6 py-5" data-id="element-895">
                <div className="text-gray-300 font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-896">
                  Specialized Capabilities
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-897">
                  Specialized Development Services
                </h3>
              </div>
              <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-898">
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-899">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-900">
                    Cross-Domain (NIPR/JWICS)
                  </div>
                  <div className="text-nrs-orange font-semibold text-sm mb-2" data-id="element-901">
                    $180–220/hr
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-902">
                    Bi-directional data sync, dual-domain architectures. TS/SCI
                    cleared developers.
                  </p>
                </div>
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-903">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-904">
                    Custom Module Development
                  </div>
                  <div className="text-nrs-orange font-semibold text-sm mb-2" data-id="element-905">
                    $50K–150K
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-906">
                    Mission-specific applications built on the ServiceNow
                    platform.
                  </p>
                </div>
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-907">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-908">
                    Integration Engineering
                  </div>
                  <div className="text-nrs-orange font-semibold text-sm mb-2" data-id="element-909">
                    $160–200/hr
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-910">
                    Complex API integrations, REST/SOAP, MID Server
                    configurations.
                  </p>
                </div>
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-911">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-912">
                    Platform Rescue & Remediation
                  </div>
                  <div className="text-nrs-orange font-semibold text-sm mb-2" data-id="element-913">
                    $75K–200K
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-914">
                    Technical debt reduction, upgrade unblocking, Instance Scan
                    remediation.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMBINED PACKAGES */}
      <section className="bg-white py-16 px-4" data-id="element-915">
        <div className="max-w-7xl mx-auto" data-id="element-916">
          <div className="text-center mb-10" data-id="element-917">
            <div className="inline-block px-3 py-1 bg-nrs-orange/10 border border-nrs-orange/30 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-3" data-id="element-918">
              Most Popular
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy" data-id="element-919">
              Combined Packages
            </h2>
            <p className="text-gray-600 mt-2" data-id="element-920">
              Governance + Development + Operations — everything under one roof
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-921">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border-2 border-nrs-orange rounded-xl overflow-hidden" data-id="element-922">
              <div className="bg-nrs-orange px-6 py-5" data-id="element-923">
                <div className="flex items-center gap-2 mb-2" data-id="element-924">
                  <StarIcon className="h-5 w-5 text-white" data-id="element-925" />
                  <span className="text-white font-heading font-bold text-xs uppercase tracking-wider" data-id="element-926">
                    Complete Solution
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-927">
                  Complete Platform Stewardship
                </h3>
                <div className="mt-2 text-orange-100 font-bold text-lg" data-id="element-928">
                  $250K–400K annually
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-929">
                <p className="text-gray-600 text-sm mb-4" data-id="element-930">
                  Governance + Development + Operations. Everything under one
                  roof.
                </p>
                <ul className="space-y-2 mb-4" data-id="element-931">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-932">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-933" />
                    Fractional Platform Owner (governance)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-934">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-935" />
                    Platform Operations & Maintenance
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-936">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-937" />
                    Development capacity (monthly sprints)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-938">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-939" />
                    Application rationalization
                  </li>
                </ul>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100" data-id="element-940">
                  <p className="text-xs text-orange-800 font-medium" data-id="element-941">
                    Best For: Small-to-medium agencies (500-5,000 users) needing
                    complete ServiceNow partnership
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border-2 border-nrs-blue rounded-xl overflow-hidden" data-id="element-942">
              <div className="bg-nrs-blue px-6 py-5" data-id="element-943">
                <div className="flex items-center gap-2 mb-2" data-id="element-944">
                  <StarIcon className="h-5 w-5 text-white" data-id="element-945" />
                  <span className="text-white font-heading font-bold text-xs uppercase tracking-wider" data-id="element-946">
                    Modernization Program
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-947">
                  Platform Modernization Program
                </h3>
                <div className="mt-2 text-blue-200 font-bold text-lg" data-id="element-948">
                  $300K–800K (12-24 months)
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-949">
                <p className="text-gray-600 text-sm mb-4" data-id="element-950">
                  Application Rationalization + Migration Development +
                  Governance.
                </p>
                <ul className="space-y-2 mb-4" data-id="element-951">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-952">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-953" />
                    60-day application rationalization
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-954">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-955" />
                    Phased migration development (6-24 months)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-956">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-957" />
                    Governance framework implementation
                  </li>
                </ul>
                <div className="bg-nrs-light rounded-lg p-3 border border-nrs-blue/20" data-id="element-958">
                  <p className="text-xs text-nrs-blue font-medium" data-id="element-959">
                    Best For: Agencies with 50+ legacy applications
                    consolidating onto ServiceNow
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-nrs-navy py-12 px-4" data-id="element-960">
        <div className="max-w-4xl mx-auto text-center" data-id="element-961">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4" data-id="element-962">
            Not Sure Which Service Fits?
          </h2>
          <p className="text-blue-200 mb-6" data-id="element-963">
            Schedule a free 30-minute platform assessment. We'll recommend the
            right engagement model for your situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-964">
            <Link to="/calculator" className="px-6 py-3 bg-nrs-orange text-white font-semibold rounded-md hover:bg-amber-500 transition-colors" data-id="element-965">
              Calculate Your Risk Score First
            </Link>
            <a href="mailto:info@newriversystems.com" className="px-6 py-3 border-2 border-white text-white font-semibold rounded-md hover:bg-white hover:text-nrs-navy transition-colors" data-id="element-966">
              Schedule Free Assessment
            </a>
          </div>
        </div>
      </section>
    </div>;
}